import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LegacyImageFormat } from '../../../../models/new/legacy-image-format';

export type LegacyImageFormatModalData = {
	format: LegacyImageFormat;
	// All formats in order, to check the size against the previous and the next one.
	formats: LegacyImageFormat[];
};

export type LegacyImageFormatModalResult = {
	size: number;
	active: boolean;
};

@Component({
	standalone: false,
	selector: 'bt-legacy-image-format-modal',
	templateUrl: './legacy-image-format-modal.component.html',
	styleUrls: ['./legacy-image-format-modal.component.scss']
})
export class LegacyImageFormatModalComponent {
	readonly #dialogRef = inject(MatDialogRef<LegacyImageFormatModalComponent>);
	readonly data = inject<LegacyImageFormatModalData>(MAT_DIALOG_DATA);

	// Same limits as the API validator (LegacyImageFormat.MinSize / MaxSize).
	readonly minSize = 16;
	readonly maxSize = 6000;

	size: number | null = this.data.format.width ?? null;
	active = !!this.data.format.active;
	validation: { size?: string; active?: string } = {};

	onSizeChange(): void {
		this.validation.size = undefined;
	}

	toggleActive(): void {
		this.active = !this.active;
		this.validation.active = undefined;
	}

	close(): void {
		this.#dialogRef.close();
	}

	apply(): void {
		this.validation = {
			size: this.getSizeError(),
			active: this.getActiveError()
		};

		if (this.validation.size || this.validation.active) {
			return;
		}

		const result: LegacyImageFormatModalResult = { size: this.size!, active: this.active };
		this.#dialogRef.close(result);
	}

	// Same check as the API: side within the limits, greater than the previous format
	// and smaller than the next one (inactive formats count too).
	private getSizeError(): string | undefined {
		if (this.size === null || this.size === undefined) {
			return 'Required field';
		}
		if (!Number.isInteger(this.size) || this.size < this.minSize || this.size > this.maxSize) {
			return `Must be a whole number between ${this.minSize} and ${this.maxSize} px`;
		}

		const index = this.data.formats.findIndex(other => other.id === this.data.format.id);
		const previous = this.data.formats[index - 1];
		const next = this.data.formats[index + 1];
		if (previous && this.size <= (previous.width ?? 0)) {
			return `Must be greater than ${previous.code} (${previous.width} px)`;
		}
		if (next && this.size >= (next.width ?? 0)) {
			return `Must be smaller than ${next.code} (${next.width} px)`;
		}
		return undefined;
	}

	private getActiveError(): string | undefined {
		const otherActive = this.data.formats.some(other => other.id !== this.data.format.id && other.active);
		return !this.active && !otherActive ? 'At least one format must stay active' : undefined;
	}
}
